import React, { useMemo, useState } from 'react';
import { DownOutlined, UpOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

export interface AuditLogEntry {
  id: string;
  timestamp: string | Date;
  action: string;
  targetType: string;
  targetId: string;
  targetName?: string;
  actor?: string;
  result: 'success' | 'warning' | 'error';
  errorMessage?: string;
  details?: Record<string, any>;
  metadata?: Record<string, any>;
}

interface AuditLogTableProps {
  logs: AuditLogEntry[];
  loading?: boolean;
  onFilterChange?: (filters: { action?: string; targetType?: string; result?: string }) => void;
}

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs,
  loading = false,
  onFilterChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<{
    action?: string;
    targetType?: string;
    result?: string;
  }>({});

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value || undefined };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const resultConfig: Record<string, { badge: string; icon: React.ReactNode }> = {
    success: {
      badge: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
      icon: <CheckCircleOutlined className="text-lg" />,
    },
    SUCCESS: {
      badge: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
      icon: <CheckCircleOutlined className="text-lg" />,
    },
    warning: {
      badge: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
      icon: <ExclamationCircleOutlined className="text-lg" />,
    },
    WARNING: {
      badge: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
      icon: <ExclamationCircleOutlined className="text-lg" />,
    },
    error: {
      badge: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400',
      icon: <CloseCircleOutlined className="text-lg" />,
    },
    ERROR: {
      badge: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400',
      icon: <CloseCircleOutlined className="text-lg" />,
    },
  };

  const getUniqueActions = (): string[] => {
    return Array.from(new Set(logs.map((log) => log.action))).sort();
  };

  const getUniqueTargetTypes = (): string[] => {
    return Array.from(new Set(logs.map((log) => log.targetType))).sort();
  };

  const formatTimestamp = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="w-full">
      {/* Filters */}
      <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Action Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              Filter by Action
            </label>
            <select
              value={filters.action || ''}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Actions</option>
              {getUniqueActions().map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>

          {/* Target Type Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              Filter by Target
            </label>
            <select
              value={filters.targetType || ''}
              onChange={(e) => handleFilterChange('targetType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Targets</option>
              {getUniqueTargetTypes().map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Result Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              Filter by Result
            </label>
            <select
              value={filters.result || ''}
              onChange={(e) => handleFilterChange('result', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Results</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 w-8" />
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 min-w-48">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 min-w-32">
                  Action
                </th>
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 min-w-32">
                  Target
                </th>
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 min-w-40">
                  Target Name
                </th>
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 min-w-28">
                  Actor
                </th>
                <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100 min-w-24">
                  Result
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                    Loading audit logs...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No audit logs available
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const isExpanded = expandedIds.has(log.id);
                  const resultConfig_ = resultConfig[log.result] ?? {
                    badge: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
                    icon: null,
                  };

                  return (
                    <React.Fragment key={log.id}>
                      {/* Main Row */}
                      <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 text-center">
                          {(log.details || log.metadata || log.errorMessage) && (
                            <button
                              onClick={() => toggleExpand(log.id)}
                              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                              aria-label="Expand details"
                            >
                              {isExpanded ? (
                                <UpOutlined />
                              ) : (
                                <DownOutlined />
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                          <span className="font-mono text-xs">
                            {formatTimestamp(log.timestamp)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100 font-medium">
                          {log.action}
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                          {log.targetType}
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                          <div>
                            <p className="font-medium">{log.targetName || log.targetId}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {log.targetId}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                          {log.actor || '—'}
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${resultConfig_.badge}`}>
                            {resultConfig_.icon}
                            {log.result.charAt(0).toUpperCase() + log.result.slice(1)}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                          <td colSpan={7} className="px-6 py-4">
                            <div className="space-y-3">
                              {log.errorMessage && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Error Message
                                  </p>
                                  <p className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded font-mono break-words">
                                    {log.errorMessage}
                                  </p>
                                </div>
                              )}

                              {log.details && Object.keys(log.details).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Details
                                  </p>
                                  <pre className="bg-gray-900 dark:bg-gray-900 text-gray-100 text-xs p-3 rounded overflow-auto max-h-48">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Metadata
                                  </p>
                                  <pre className="bg-gray-900 dark:bg-gray-900 text-gray-100 text-xs p-3 rounded overflow-auto max-h-48">
                                    {JSON.stringify(log.metadata, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogTable;
